import * as XLSX from "xlsx";
import { useState, useMemo } from "react";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetDailyRentTotalsQuery } from "../../slices/rentsApiSlice";   // ✅ new hook
import { useGetPaymentsQuery } from "../../slices/paymentsApiSlice";

export default function Balance() {
  const today = new Date().toISOString().split("T")[0];
  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);

  // Queries
  const { data: salesData, isLoading: salesLoading } = useGetSalesQuery({ startDate: date1, endDate: date2 });
  const { data: expensesData, isLoading: expensesLoading } = useGetExpensesQuery({ startDate: date1, endDate: date2 });
  const { data: incomesData, isLoading: incomesLoading } = useGetIncomesQuery({ startDate: date1, endDate: date2 });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({ startDate: date1, endDate: date2 });
  const { data: suppliesData, isLoading: suppliesLoading } = useGetSuppliesQuery({ startDate: date1, endDate: date2 });
  const { data: rentsData, isLoading: rentsLoading } = useGetDailyRentTotalsQuery({ start: date1, end: date2 }); // ✅ use daily totals
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentsQuery({ startDate: date1, endDate: date2 });

  // Helpers
  const formatDate = (d) => new Date(d).toISOString().split("T")[0];
  const getDatesBetween = (start, end) => {
    const dates = [];
    let current = new Date(start);
    while (current <= new Date(end)) {
      dates.push(formatDate(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const sumByDate = (items, field, date, dateField = "date") =>
    items?.filter((i) => i[dateField] && formatDate(i[dateField]) === date)
      .reduce((sum, i) => sum + Number(i[field] || 0), 0) || 0;

  // Calculate report
  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);
    const result = dates.map((date) => {
      const netSales = sumByDate(salesData?.sales, "totalPrice", date, "date");
      const expenses = sumByDate(expensesData?.expenses, "amount", date, "date");
      const payments = sumByDate(paymentsData?.payments, "amount", date, "date");
      const netExpenses = expenses + payments;

      const cashCollected = sumByDate(incomesData?.incomes, "amount", date, "date");
      const depositTotal = sumByDate(depositsData?.deposits, "amount", date, "date");
      const netBank = depositTotal > 0 ? depositTotal : depositTotal;

      const purchases = -sumByDate(suppliesData?.supplies, "cardCost", date, "date");

      // ✅ Rent: now use aggregated daily totals from backend
      const rentTotal = rentsData?.find((r) => r._id === date)?.totalRent || 0;

      const netCash = netSales + netExpenses + rentTotal;
      const difference = cashCollected - netCash;
      const cashInHand = netCash - netBank + purchases;

      return { date, netSales, netExpenses, rentTotal, netCash, cashCollected, difference, netBank, purchases, cashInHand };
    });

    setReportData(result);
  };

  // Totals
  const totals = useMemo(() => {
    const sum = (field) => reportData.reduce((acc, row) => acc + (row[field] || 0), 0);
    const totalSales = sum("netSales");
    const totalExpenses = sum("netExpenses");
    const totalRents = sum("rentTotal");
    const totalIncomes = sum("cashCollected");
    const totalDeposit = sum("netBank");
    const totalPurchases = sum("purchases");
    const totalCashInHand = sum("cashInHand");
    const totalNetCash = totalSales + totalExpenses + totalRents;
    const totalDifference = totalIncomes - totalNetCash;

    return {
      totalSales, totalExpenses, totalRents, totalIncomes,
      totalDeposit, totalPurchases, totalCashInHand,
      totalNetCash, totalDifference,
    };
  }, [reportData]);

  // Export
  const exportToExcel = () => {
    const exportData = [
      ...reportData.map((row) => ({
        Date: row.date,
        NetSales: row.netSales.toFixed(2),
        NetExpenses: row.netExpenses.toFixed(2),
        Rent: row.rentTotal.toFixed(2),
        NetCash: row.netCash.toFixed(2),
        CashCollected: row.cashCollected.toFixed(2),
        Difference: row.difference.toFixed(2),
        NetBank: row.netBank.toFixed(2),
        Purchases: row.purchases.toFixed(2),
        CashInHand: row.cashInHand.toFixed(2),
      })),
      {
        Date: "TOTAL",
        NetSales: totals.totalSales.toFixed(2),
        NetExpenses: totals.totalExpenses.toFixed(2),
        Rent: totals.totalRents.toFixed(2),
        NetCash: totals.totalNetCash.toFixed(2),
        CashCollected: totals.totalIncomes.toFixed(2),
        Difference: totals.totalDifference.toFixed(2),
        NetBank: totals.totalDeposit.toFixed(2),
        Purchases: totals.totalPurchases.toFixed(2),
        CashInHand: totals.totalCashInHand.toFixed(2),
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };

  const loading = salesLoading || expensesLoading || incomesLoading || depositsLoading || suppliesLoading || rentsLoading || paymentsLoading;

    return (
    <div className="balance-report">
      <h1>Balance Report</h1>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={date1} onChange={(e) => setDate1(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={date2} onChange={(e) => setDate2(e.target.value)} />
        </label>
      </div>

      <button onClick={calculateReport} className="calculate-button">Calculate Report</button>

      {loading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Net Sales</th>
                <th>Net Exp/Payments</th>
                <th>Rent</th>
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.netSales.toFixed(2)}</td>
                  <td>{row.netExpenses.toFixed(2)}</td>
                  <td>{row.rentTotal.toFixed(2)}</td>
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>{totals.totalSales.toFixed(2)}</strong></td>
                <td><strong>{totals.totalExpenses.toFixed(2)}</strong></td>
                <td><strong>{totals.totalRents.toFixed(2)}</strong></td>
                <td><strong>{totals.totalNetCash.toFixed(2)}</strong></td>
                <td><strong>{totals.totalIncomes.toFixed(2)}</strong></td>
                <td><strong>{totals.totalDifference.toFixed(2)}</strong></td>
                <td><strong>{totals.totalDeposit.toFixed(2)}</strong></td>
                <td><strong>{totals.totalPurchases.toFixed(2)}</strong></td>
                <td><strong>{totals.totalCashInHand.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>

          <button onClick={exportToExcel} className="export-button">
            Export to Excel
          </button>
        </>
      )}
    </div>
  );
}